import React from 'react'
import { makeStyles, Typography } from '@material-ui/core'

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#FFFFFF',
        minHeight: 0,
        overflow: 'hidden',
    },

    rightHeader: {
        background: '#FFFFFF',
        padding: '20px 28px 10px 28px',
        flexShrink: 0,
    },

    panelTitle: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '22px',
        fontWeight: 700,
        color: '#000000',
        lineHeight: '30px',
    },

    panelSubTitle: {
        marginTop: '8px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '15px',
        fontWeight: 400,
        color: '#5F6368',
        lineHeight: '22px',
    },

    bodyWrapper: {
        padding: '0 24px 12px 24px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },

    consentBox: {
        border: '1px solid #D9DDE7',
        borderRadius: '14px',
        background: '#FAFAFB',
        padding: '20px 22px',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
    },

    sectionHeading: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px',
        fontWeight: 700,
        color: '#000000',
        lineHeight: '28px',
        marginBottom: '12px',
    },

    subheadingText: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px',
        fontWeight: 600,
        color: '#000000',
        lineHeight: '28px',
        marginTop: '14px',
        marginBottom: '6px',
    },

    normalText: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px',
        fontWeight: 300,
        color: '#000000',
        lineHeight: '28px',
        marginBottom: '14px',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },

    alink: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px',
        fontWeight: 400,
        color: '#5571C6',
        textDecoration: 'underline',
    },

    list: {
        margin: 0,
        paddingLeft: '22px',
    },

    listItem: {
        marginBottom: '12px',
        color: '#111111',
    },
}))

const IntakeTherapyModal = ({ therapyConsentStep }) => {
    const classes = useStyles()
    const isStep1 = therapyConsentStep === 1

    return (
        <div className={classes.root}>
            <div className={classes.rightHeader}>
                <Typography className={classes.panelTitle}>
                    {isStep1 ? 'Associate Consent' : 'Psychotherapy Consent'}
                </Typography>

                <Typography className={classes.panelSubTitle}>
                    {isStep1
                        ? 'You are booking with an associate therapist, please read below.'
                        : 'Please review and acknowledge the psychotherapy consent information below.'}
                </Typography>
            </div>

            <div className={classes.bodyWrapper}>
                <div className={classes.consentBox}>
                    {isStep1 && (
                        <>
                            <Typography className={classes.normalText}>
                                I understand that I will be receiving therapy services from an
                                Associate Therapist who is currently completing the required
                                clinical hours to obtain full licensure as an independent
                                therapist/counselor in the state of California. To meet the
                                licensure requirements set by the California Board of Behavioral
                                Sciences (BBS), Associate Therapists must complete 3,000
                                supervised hours under the guidance of a qualified clinical
                                supervisor approved by the Board. This supervision is essential for
                                professional development and progression toward independent
                                licensure.
                            </Typography>

                            <Typography className={classes.normalText}>
                                I acknowledge that therapy services will be based on a relationship
                                built on trust and respect. Together, we will identify goals for
                                therapy and work collaboratively toward achieving them. Sessions may
                                involve exploring thoughts, emotions, personal history,
                                communication patterns, beliefs, and areas for personal growth.
                            </Typography>

                            <Typography className={classes.normalText}>
                                As part of the training process, the Associate Therapist may
                                consult with their supervisor regarding my case to ensure I receive
                                the highest quality of care. Additionally, there may be occasions
                                when a supervisor observes a portion of my therapy sessions for
                                training and quality assurance purposes. Observations may occur in
                                person or via a secure video link. The supervisor will not actively
                                participate in the session but will provide feedback to support the
                                therapist’s clinical growth. I will be informed in advance if a
                                supervisor will be present in a session.
                            </Typography>

                            <Typography className={classes.normalText}>
                                I understand that live observation enables the Associate Therapist
                                to gain skills and allows their supervisor to assess, evaluate, and
                                better understand their abilities. Both the Associate Therapist and
                                their supervisor are bound by HIPAA and all applicable
                                confidentiality laws. Any information shared during supervision or
                                observation will remain strictly confidential and will only be used
                                for professional development and supervisory purposes. No audio or
                                video recording of these sessions will take place.
                            </Typography>

                            <Typography className={classes.normalText}>
                                I have the right to decline observation at any time, and my
                                decision will not impact my treatment. If an observation is
                                scheduled, I will be informed in advance.
                            </Typography>

                            <Typography className={classes.normalText}>
                                I have the right to know who is involved in my care. At the time of
                                my appointment, I will be provided with the name and license number
                                of the clinical supervisor for my reference.
                            </Typography>

                            <Typography className={classes.normalText}>
                                To ensure I am comfortable with my provider, I also have the
                                ability to review a full list of providers by visiting{' '}
                                <a
                                    className={classes.alink}
                                    href="https://www.telemed2u.com/our-providers"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Our Providers
                                </a>
                                .
                            </Typography>
                        </>
                    )}

                    {!isStep1 && (
                        <>
                            <Typography className={classes.sectionHeading}>
                                Psychotherapy Consent
                            </Typography>

                            <Typography className={classes.normalText}>
                                I, the patient, consent to participate in telemental health
                                services with a TeleMed2U provider as part of my psychotherapy...
                            </Typography>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default IntakeTherapyModal